import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import PageHeader from '../../components/UI/PageHeader';
import Container from '../../components/UI/Container';
import SEOHead from '../../components/SEO/SEOHead';
import ContactForm from './ContactForm';
import { useSystemSettings } from '../../contexts/SystemSettingsContext';

const Contact: React.FC = () => {
  const { settings } = useSystemSettings();

  return (
    <>
      <SEOHead 
        title="Contact - Adopție Câini București"
        description="Contactează Adăpostul Odăi pentru adopție câini în București, Sector 1. Telefon: +40 750 209 751, Email: protectiaanimalelor@primarias1.ro. Program: Luni-Vineri 10:00-18:00, Sâmbătă 10:00-16:00."
        keywords="contact adaspost caini bucuresti, adopta caini sector 1, telefon adaspost odai, email protectia animalelor, program vizitare adaspost, contact adopție câini"
        url="/contact"
        type="website"
        localBusiness={true}
        image="https://images.pexels.com/photos/406014/pexels-photo-406014.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />
      <PageHeader
        title="Contactează-ne"
        subtitle="Suntem aici pentru a răspunde la toate întrebările tale despre adopție, donații sau voluntariat"
        image="https://images.pexels.com/photos/406014/pexels-photo-406014.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
      />

      <Container>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-bold text-primary mb-6 font-display">Informații de contact</h2>

            <div className="space-y-6 mb-8">
              <div className="flex">
                <div className="bg-primary-100 p-3 rounded-full mr-4">
                  <MapPin className="text-primary w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">Adresa noastră</h3>
                  <p className="text-gray-600">{settings?.address || 'Sos. Odăi, nr. 3-5, Sector 1, București'}</p>
                </div>
              </div>

              <div className="flex">
                <div className="bg-primary-100 p-3 rounded-full mr-4">
                  <Phone className="text-primary w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">Telefon</h3>
                  <p className="text-gray-600">{settings?.phone || '+40 750 209 751'}</p>
                </div>
              </div>

              <div className="flex">
                <div className="bg-primary-100 p-3 rounded-full mr-4">
                  <Mail className="text-primary w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">Email</h3>
                  <p className="text-gray-600">{settings?.email || 'protectiaanimalelor@primarias1.ro'}</p>
                </div>
              </div>

              <div className="flex">
                <div className="bg-primary-100 p-3 rounded-full mr-4">
                  <Clock className="text-primary w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">Program de vizitare</h3>
                  <p className="text-gray-600">{settings?.operatingHours || 'Luni - Vineri: 10:00 - 18:00'}</p>
                  <p className="text-gray-600">Sâmbătă: 10:00 - 16:00</p>
                  <p className="text-gray-600">Duminică: Închis</p>
                </div>
              </div>
            </div>

            <div className="aspect-video rounded-lg overflow-hidden shadow-md">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2847.123456789!2d26.0987654321!3d44.4876543210!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x40b1ff3a9bf2f8f9%3A0xb3eb3a55b229ef13!2sSos.%20Od%C4%83i%203-5%2C%20Sector%201%2C%20Bucure%C8%99ti!5e0!3m2!1sro!2sro!4v1650532574428!5m2!1sro!2sro" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title={`Locația adăpostului - ${settings?.address}`}
              ></iframe>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="bg-white rounded-lg shadow-md p-6 lg:p-8">
              <div className="flex items-center mb-6">
                <Send className="text-accent w-6 h-6 mr-3" />
                <h2 className="text-2xl font-bold text-primary font-display">Trimite-ne un mesaj</h2>
              </div>
              <p className="text-gray-600 mb-6">
                Ai întrebări despre adopție, donații sau despre unul dintre câinii noștri? Completează formularul de mai jos și te vom contacta cât mai curând posibil.
              </p>
              <ContactForm />
            </div>
          </motion.div>
        </div>

        <div className="mt-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-primary-100 rounded-lg p-8"
          >
            <h2 className="text-2xl font-bold text-primary mb-6 text-center font-display">Întrebări frecvente</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-bold text-primary mb-2">Care sunt orele de vizitare?</h3>
                <p className="text-gray-600">
                  Poți vizita adăpostul nostru de luni până vineri între orele 10:00 și 18:00, iar sâmbăta între 10:00 și 16:00. Duminica suntem închiși.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-bold text-primary mb-2">Ce documente îmi trebuie pentru adopție?</h3>
                <p className="text-gray-600">
                  Pentru adopție ai nevoie de cartea de identitate și să completezi formularul nostru de adopție. De asemenea, va trebui să semnezi și contractul de adopție.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-bold text-primary mb-2">Există o taxă de adopție?</h3>
                <p className="text-gray-600">
                  Nu, adopțiile sunt gratuite! Adăpostul acoperă toate costurile de microcipare, vaccinare, deparazitare și carnetul de sănătate al animalului.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="font-bold text-primary mb-2">Pot vizita adăpostul fără programare?</h3>
                <p className="text-gray-600">
                  Da, poți veni fără programare în timpul orelor de vizitare, dar îți recomandăm să ne contactezi în prealabil pentru a ne asigura că va fi cineva disponibil să te ghideze.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </>
  );
};

export default Contact;